/**
 * VerdictLine (#1315) — the demo's headline (beat 1): an Ampel paired
 * with a German word + the verdict sentence. Colour never carries
 * meaning alone — each level has an icon and a word.
 */
import { AlertTriangle, CheckCircle2, TrendingDown } from 'lucide-react';
import type { VerdictLevel, Verdict } from './types';
import './VerdictLine.css';

const LABELS: Record<VerdictLevel, { word: string; Icon: typeof CheckCircle2 }> = {
  green: { word: 'Grün', Icon: CheckCircle2 },
  yellow: { word: 'Eng', Icon: AlertTriangle },
  red: { word: 'Engpass', Icon: TrendingDown },
};

/**
 * Drop the badge word when the sentence opens with it.
 *
 * Every producer writes the level word first — ``computeBalanceSeries`` and its
 * server-side mirror ``liquiditaet_reads._verdict`` both emit „Grün bis …" /
 * „Eng ab …" / „Engpass ab …" — which is right for a sentence read on its own
 * (the assistant quotes it, admin-MCP returns it) and a stutter next to a badge
 * already showing that word. Trim for display only: the badge supplies the
 * word, so the pair still reads „Engpass" + „ab 14. Januar 2027 …".
 *
 * The trailing space in the test is what makes this safe: „Eng" is a prefix of
 * „Engpass", and a bare ``startsWith(word)`` on a yellow verdict carrying a red
 * sentence would render „pass ab …". A sentence that does not open with its
 * word is returned untouched.
 */
function withoutLeadingWord(sentence: string, word: string): string {
  return sentence.startsWith(`${word} `) ? sentence.slice(word.length + 1) : sentence;
}

export default function VerdictLine({
  verdict,
  sentence,
}: {
  verdict: Verdict;
  /** Overrides ``verdict.sentence`` — lets the summary show a day-precise risk line. */
  sentence?: string;
}) {
  const { word, Icon } = LABELS[verdict.level];
  const full = sentence ?? verdict.sentence;
  return (
    <div
      className={`liquiditaetsplanung-verdict liquiditaetsplanung-verdict--${verdict.level}`}
      role="status"
    >
      <span className="liquiditaetsplanung-verdict__badge">
        <Icon size={18} aria-hidden="true" />
        <span className="liquiditaetsplanung-verdict__word">{word}</span>
      </span>
      {/* ``title`` carries the sentence as written, echo included, because the
          bar clips it (HOF-5) and the pointer is the way back to the amounts a
          red verdict names. */}
      <span className="liquiditaetsplanung-verdict__sentence" title={full}>
        {withoutLeadingWord(full, word)}
      </span>
    </div>
  );
}
